export interface ProcessImageOptions {
  maxWidth?: number;
  maxHeight?: number;
  targetWidth?: number;
  targetHeight?: number;
  aspectRatio?: number | null;         // null/undefined => mantener original
  fit?: 'cover' | 'contain' | 'smart'; // smart = cover si el recorte < tolerancia, si no contain
  smartCropTolerance?: number;         // % máximo de área que se permite perder (0..1) en smart (default 0.22)
  upscale?: boolean;
  background?: string;                 // color o 'blur'
  outputType?: 'image/webp' | 'image/jpeg';
  quality?: number;
  generateBlurPlaceholder?: boolean;   // genera base64 blur para skeletons
  blurSize?: number;                   // tamaño base del blur (default 40)
}

export interface ProcessedImageResult {
  file: File;
  width: number;
  height: number;
  placeholderBlurDataUrl?: string;
}

export async function processImageFile(
  file: File,
  opts: ProcessImageOptions = {}
): Promise<ProcessedImageResult> {
  const {
    maxWidth = 1600,
    maxHeight = 900,
    targetWidth,
    targetHeight,
    aspectRatio = null,
    fit = 'cover',
    smartCropTolerance = 0.22,
    upscale = true,
    background = '#000',
    outputType = 'image/webp',
    quality = 0.85,
    generateBlurPlaceholder = true,
    blurSize = 40
  } = opts;

  const arrayBuffer = await file.arrayBuffer();
  const blob = new Blob([arrayBuffer]);
  const img = await createImageBitmap(blob);

  const srcW = img.width;
  const srcH = img.height;
  const originalAspect = srcW / srcH;
  const targetAspect = aspectRatio || originalAspect;

  // Determinar ancho/alto base deseado
  let baseW = targetWidth ?? Math.min(maxWidth, srcW);
  if (aspectRatio) {
    baseW = targetWidth
      ? targetWidth
      : Math.min(
          maxWidth,
          Math.round(
            originalAspect > targetAspect
              ? Math.min(srcW, maxWidth)
              : (srcH * targetAspect)
          )
        );
  }
  let baseH = targetHeight
    ? targetHeight
    : Math.round(baseW / targetAspect);

  if (baseH > maxHeight) {
    baseH = maxHeight;
    baseW = Math.round(baseH * targetAspect);
  }

  // Upscale control
  if (!upscale) {
    if (srcW < baseW || srcH < baseH) {
      // escala máxima sin superar original
      const scale = Math.min(srcW / baseW, srcH / baseH);
      baseW = Math.round(baseW * scale);
      baseH = Math.round(baseH * scale);
    }
  } else {
    // permitir upscale pero nunca superar max
    baseW = Math.min(baseW, maxWidth);
    baseH = Math.min(baseH, maxHeight);
  }

  // Cálculo de recorte (solo si cover o smart)
  let mode: 'cover' | 'contain' = fit === 'contain' ? 'contain' : 'cover';
  if (fit === 'smart') {
    // Área que se perdería si aplicamos cover
    const coverCropInfo = computeCoverCrop(srcW, srcH, targetAspect);
    const keptArea = coverCropInfo.sW * coverCropInfo.sH;
    const areaLossRatio = 1 - keptArea / (srcW * srcH);
    if (areaLossRatio > smartCropTolerance) {
      mode = 'contain';
    } else {
      mode = 'cover';
    }
  }

  // Canvas final
  const outW = baseW;
  const outH = baseH;
  const canvas = document.createElement('canvas');
  canvas.width = outW;
  canvas.height = outH;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return { file, width: srcW, height: srcH };
  }

  if (mode === 'cover') {
    const { sx, sy, sW, sH } = computeCoverCrop(srcW, srcH, targetAspect);
    ctx.drawImage(img, sx, sy, sW, sH, 0, 0, outW, outH);
  } else {
    // contain
    if (background === 'blur') {
      // dibujar fondo blur
      drawBlurBackground(ctx, img, outW, outH, originalAspect, targetAspect);
    } else {
      ctx.fillStyle = background;
      ctx.fillRect(0, 0, outW, outH);
    }
    const scale = Math.min(outW / srcW, outH / srcH);
    const drawW = Math.round(srcW * scale);
    const drawH = Math.round(srcH * scale);
    const dx = Math.round((outW - drawW) / 2);
    const dy = Math.round((outH - drawH) / 2);
    ctx.drawImage(img, 0, 0, srcW, srcH, dx, dy, drawW, drawH);
  }

  const blobOut: Blob = await new Promise(resolve =>
    canvas.toBlob(b => resolve(b || blob), outputType, quality)
  );

  const ext = outputType === 'image/webp' ? 'webp' : 'jpg';
  const newName =
    file.name.replace(/\.[^.]+$/, '') +
    `-${outW}x${outH}-${mode}.${ext}`;

  const processedFile = new File([blobOut], newName, {
    type: outputType,
    lastModified: Date.now()
  });

  let placeholderBlurDataUrl: string | undefined;
  if (generateBlurPlaceholder) {
    placeholderBlurDataUrl = await buildBlurDataUrl(img, blurSize, outputType);
  }

  return {
    file: processedFile,
    width: outW,
    height: outH,
    placeholderBlurDataUrl
  };
}

/* Helpers */

function computeCoverCrop(srcW: number, srcH: number, targetAspect: number) {
  const srcAspect = srcW / srcH;
  let sx = 0,
    sy = 0,
    sW = srcW,
    sH = srcH;
  if (Math.abs(srcAspect - targetAspect) > 0.0001) {
    if (srcAspect > targetAspect) {
      // recorte horizontal
      sW = Math.round(srcH * targetAspect);
      sx = Math.round((srcW - sW) / 2);
    } else {
      // recorte vertical
      sH = Math.round(srcW / targetAspect);
      sy = Math.round((srcH - sH) / 2);
    }
  }
  return { sx, sy, sW, sH };
}

function drawBlurBackground(
  ctx: CanvasRenderingContext2D,
  img: ImageBitmap,
  outW: number,
  outH: number,
  srcAspect: number,
  targetAspect: number
) {
  // Llenar con un blur "manual" (escalar muy pequeño y re-escalar)
  const tmp = document.createElement('canvas');
  const blurBaseW = 32;
  const blurBaseH = Math.round(blurBaseW / targetAspect);
  tmp.width = blurBaseW;
  tmp.height = blurBaseH;
  const tctx = tmp.getContext('2d');
  if (tctx) {
    // cubrir para que no queden bordes
    const { sx, sy, sW, sH } = computeCoverCrop(img.width, img.height, targetAspect);
    tctx.drawImage(img, sx, sy, sW, sH, 0, 0, blurBaseW, blurBaseH);
  }
  ctx.imageSmoothingEnabled = true;
  ctx.drawImage(tmp, 0, 0, blurBaseW, blurBaseH, 0, 0, outW, outH);
  ctx.globalAlpha = 0.35;
  ctx.fillStyle = '#000';
  ctx.fillRect(0, 0, outW, outH);
  ctx.globalAlpha = 1;
}

async function buildBlurDataUrl(
  img: ImageBitmap,
  size: number,
  outputType: string
): Promise<string> {
  const canvas = document.createElement('canvas');
  const ratio = img.width / img.height;
  let w = size;
  let h = Math.round(w / ratio);
  if (h > size) {
    h = size;
    w = Math.round(h * ratio);
  }
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) return '';
  ctx.drawImage(img, 0, 0, img.width, img.height, 0, 0, w, h);
  return canvas.toDataURL(outputType, 0.6);
}